import type { LocalDate } from '../dates';
import type { Mutation, MutationOp } from '../mutations';
import type { BuildCtx } from '../spending/types';

/** Limit starts at startLimit on `from` and drops by `step` every `everyDays` days, never below `floor`. */
export interface TaperSegment {
  from: LocalDate;
  startLimit: number;
  step: number;
  everyDays: number;
  floor: number;
}

export type Price =
  | { kind: 'pack'; packPriceMinor: number; perPack: number }
  | { kind: 'stick'; stickPriceMinor: number }
  | { kind: 'none' };

export interface QuitPlan {
  id: string;
  baselinePerDay: number;
  startDate: LocalDate;
  taper: TaperSegment[];
  price: Price;
  fundId: string | null;
  triggerPlans: Record<string, string>;
  quitlineCountry: string | null;
}

export type SmokeKind = 'tap' | 'day_total';

export interface SmokeLogLike {
  id: string;
  at: string;
  date: LocalDate;
  kind: SmokeKind;
  count: number;
  costMinor: number;
  trigger: string | null;
  deletedAt: string | null;
}

export interface CravingLike {
  id: string;
  at: string;
  date: LocalDate;
  resisted: boolean;
  trigger: string | null;
  delayUsed: boolean;
  deletedAt: string | null;
}

export type QuitResult<T, E extends string> = ({ ok: true } & T) | { ok: false; error: E };

/** RESERVED_KEYS (v0.1a): set by the appliers; a builder that puts them in values is a bug. */
const APPLIER_KEYS = ['id', 'userId', 'createdAt', 'updatedAt', 'deletedAt', 'clock'] as const;

/** A mutation from Quit Smoking. Built directly so tests control ids through ctx.newId. */
export function quitMutation(
  ctx: BuildCtx,
  table: string,
  op: MutationOp,
  rowId: string,
  values?: Record<string, unknown>,
  opts: { ifAbsent?: true } = {},
): Mutation {
  for (const k of APPLIER_KEYS) {
    if (values && k in values) throw new Error(`Mutation values must not include '${k}'`);
  }
  return {
    id: ctx.newId(),
    table,
    op,
    rowId,
    ...(values ? { values } : {}),
    at: ctx.now,
    source: 'quit_smoking',
    ...(opts.ifAbsent ? { ifAbsent: true as const } : {}),
  };
}
